import React, { useEffect, useState, useRef, useImperativeHandle, useMemo } from 'react';
import {
    TextField,
    Callout,
    FontWeights,
    Text,
    DirectionalHint,
    TooltipHost,
    FontIcon,
    makeStyles,
    useTheme,
    Spinner,
    SpinnerSize,
    Stack,
} from '@fluentui/react';
// import TimeRow from '../../Model/TimePeriodRowM';
import { GetNumberSep, MathToFixed, getEmptyGuid } from '../Utils';
import { useId } from '@fluentui/react-hooks';
import { constants } from '../../Styles/constants';
import { PeriodDay } from '../../types/PeriodDay';
import { useCellCtx } from '../Contexts/CellContext';
import { parseNumber, convertHoursToProcent, getPeriodHours, convertProcentToHours, isValidNumber, getCapacityPrDay } from './Cell.helpers';
import { EndPointSolidIcon } from '@fluentui/react-icons-mdl2';
import { roundToNearestValueStep } from '../../Mobile/mobileUtils';
import { TimeEntry, UserRegistrationPeriod } from '../../api/generated/data-contracts';
import { useGridContext } from '../Contexts/GridContext/context';
import { useConfigValuesState } from '../../hooks/useConfigValue';
import { useTranslation } from 'react-i18next';
import { getPeriodDaysEnabled } from '../../helpers/periodDays';
import { useMutation } from '@tanstack/react-query';

interface propsCell {
    time: UserRegistrationPeriod;
    timeentries: TimeEntry[];
    // day: PeriodDay,
    perioddays: PeriodDay[];
    updated: (
        time: UserRegistrationPeriod,
        periodDays: PeriodDay[],
        te: any,
        cellValue: number,
        allCellsValue: number,
        comment: string,
        flagged: boolean,
        prevVal: number,
    ) => Promise<string[]>;
    readonly: boolean;
    readonlyreason: string;
    // propertyUpdated: string;
    cellDataNeedsSave: Function;
    reportPeriodId: string;
}

const CellWeek = React.forwardRef<any, propsCell>((props, ref) => {
    const { t } = useTranslation('timesheet');
    const theme = useTheme();
    const cellCtx = useCellCtx();
    const styles = getStyles();
    const textFieldRef = useRef<any>();
    const targetId = useId();

    const [celltext, setCelltext] = useState('');
    const [cellFlagged, setCellFlagged] = useState<boolean>(props.timeentries[0]?.flagged || false);
    const [showFlagIcon, setShowFlagIcon] = useState<boolean>(props.timeentries[0]?.flagged || false);
    const [cellnumber, setCellnumber] = useState<number>(0);
    const [cellComment, setCellComment] = useState<string>('');
    const [cellnumberlastsaved, setCellnumberlastsaved] = useState<number>(0);
    const [commentlastsaved, setCommentlastsaved] = useState<string>('');
    const [cellIsError, setCellIsError] = useState<string>('');
    const [showdetailsicon, setShowdetailsicon] = useState<boolean>(false);
    const [showdetailsflyout, setShowdetailsflyout] = useState<boolean>(false);
    const [textinfocus, setTextinfocus] = useState<boolean>(false);
    const [loaded, setLoaded] = useState<boolean>(false);

    const [anyChanges, setAnyChanges] = useState<boolean>(false);
    const [dataSaved, setDataSaved] = useState<boolean>(false);

    const { setCellsWithError, cellsWithError } = useGridContext();

    const {
        initialData: {
            configuration: { timeEntryRoundingInterval, gridInputType, allowCellComment, allowCellFlag, capacityDefault, weekConfig },
            aggregatedUser: { user },
        },
    } = useConfigValuesState();

    const userCapacity = useMemo(() => user.availability ?? user.calendar?.dailyCapacity ?? capacityDefault, [user, capacityDefault]);

    const workingDays = useMemo(() => {
        return getPeriodDaysEnabled(props.perioddays, t, weekConfig);
    }, [props.perioddays, t, weekConfig]);

    useEffect(() => {
        const hours = getPeriodHours(props.timeentries);
        const formattedCellValue =
            gridInputType === 'percent' ? convertHoursToProcent(hours, workingDays.length, getCapacityPrDay(workingDays, userCapacity)) : hours;
        const roundedCellValue = roundToNearestValueStep(formattedCellValue, timeEntryRoundingInterval);
        const value =
            gridInputType === 'percent'
                ? convertProcentToHours(roundedCellValue, workingDays.length, getCapacityPrDay(workingDays, userCapacity))
                : roundedCellValue;
        setCelltext(`${roundedCellValue}`);
        setCellnumber(value);
        setCellnumberlastsaved(value);

        const newComment = props.timeentries?.[0]?.comment || '';
        setCellComment(newComment);
        setCommentlastsaved(newComment);

        setLoaded(true);
    }, [props.timeentries, props.timeentries?.[0]]);

    useEffect(() => {
        const hours = getPeriodHours(props.timeentries);
        const formattedCellValue =
            gridInputType === 'percent' ? convertHoursToProcent(hours, workingDays.length, getCapacityPrDay(workingDays, userCapacity)) : hours;
        const roundedCellValue = roundToNearestValueStep(formattedCellValue, timeEntryRoundingInterval);
        setCelltext(`${roundedCellValue}`);
    }, [props]);

    useEffect(() => {
        if (showdetailsflyout === false && textinfocus === false && loaded === true) {
            console.debug('Cell is error changed: ' + cellIsError);

            const formattedCellValue =
                gridInputType === 'percent' ? convertHoursToProcent(cellnumber, workingDays.length, getCapacityPrDay(workingDays, userCapacity)) : cellnumber;
            const roundedCellValue = roundToNearestValueStep(formattedCellValue, timeEntryRoundingInterval);
            const hours =
                gridInputType === 'percent'
                    ? convertProcentToHours(roundedCellValue, workingDays.length, getCapacityPrDay(workingDays, userCapacity))
                    : roundedCellValue;
            if (cellIsError == '' && (hours !== cellnumberlastsaved || cellComment !== commentlastsaved)) {
                setDataSaved(true);
                saveData(hours, cellComment, cellnumberlastsaved, cellFlagged);
                setAnyChanges(false);
            } else {
                const formattedCellValue =
                    gridInputType === 'percent'
                        ? convertHoursToProcent(cellnumberlastsaved, workingDays.length, getCapacityPrDay(workingDays, userCapacity))
                        : cellnumberlastsaved;
                const roundedCellValue = roundToNearestValueStep(formattedCellValue, timeEntryRoundingInterval);
                const value =
                    gridInputType === 'percent'
                        ? convertProcentToHours(roundedCellValue, workingDays.length, getCapacityPrDay(workingDays, userCapacity))
                        : roundedCellValue;
                setCelltext(`${roundedCellValue}`);
                setCellnumber(value);
                setCellnumberlastsaved(value);
            }
            props.cellDataNeedsSave(false);
        }
    }, [showdetailsflyout, textinfocus]);

    useEffect(() => {
        if (cellIsError == '') {
            // Check if ctx contains cell, if so, remove it
            var newArray = cellsWithError.filter(c => c != targetId);
            setCellsWithError(newArray);
        } else {
            //Check if ctx contains cell, if not, add it
            if (cellsWithError.filter(c => c == targetId).length < 1) {
                var newArray = cellsWithError;
                newArray.push(targetId);
                setCellsWithError(newArray);
            }
        }
    }, [cellIsError]);

    useEffect(() => {
        if (textinfocus) {
            textFieldRef.current.select();
        }
    }, [textinfocus]);

    const onCellUpdated = (e: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>, text: string): void => {
        if (isAllowedKey(e)) {
            setAnyChanges(true);
            const hours =
                gridInputType === 'percent' ? convertProcentToHours(Number(text), workingDays.length, getCapacityPrDay(workingDays, userCapacity)) : text;

            isValid(GetNumberSep() === ',' ? text.replace(/\./g, ',') : text.replace(/\,/g, '.'));
            setCelltext(GetNumberSep() === ',' ? text.replace(/\./g, ',') : text.replace(/\,/g, '.'));
            setCellnumber(parseNumber(hours.toString().replace(/\,/g, '.')));
        }
    };

    const onCommentUpdated = (ev: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>, text: string): void => {
        setCellComment(text);
    };

    const onBlur = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
        setTimeout(() => {
            setTextinfocus(false);
            setShowdetailsicon(false);
            setShowFlagIcon(cellFlagged);
        }, 500);
    };

    const isValid = (value: string) => {
        if (gridInputType === 'percent' && userCapacity === 0) {
            setCellIsError(t('cell.error.noCapacity'));
            return '';
        }
        const maxHoursPerPeriod = 24 * workingDays.length;
        const parsed = value.replace(/,/g, '.');
        const newVal =
            gridInputType === 'percent' ? `${convertProcentToHours(Number(parsed), workingDays.length, getCapacityPrDay(workingDays, userCapacity))}` : parsed;
        const maxHoursMessage = t('cell.error.maxhours', { value: maxHoursPerPeriod });
        const negativeMessage = t('cell.error.negative');
        const invalidMessage = t('cell.error.invalid');
        if (isValidNumber(newVal)) {
            const newValNum = parseFloat(newVal);
            if (newValNum > maxHoursPerPeriod) {
                setCellIsError(maxHoursMessage);
                return '';
            }
            if (newValNum < 0) {
                setCellIsError(negativeMessage);
                return '';
            }
            setCellIsError('');
            return '';
        } else {
            setCellIsError(invalidMessage);
            return '';
        }
    };

    const onFocus = (event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
        props.cellDataNeedsSave(true);
        setShowdetailsicon(true);
        setShowFlagIcon(true);
        setTextinfocus(true);
    };

    const onIconClick = (ev: React.MouseEvent<HTMLElement, MouseEvent>): void => {
        setShowdetailsflyout(true);
        setShowdetailsicon(true);
        setShowFlagIcon(true);
    };

    const onFlagIconClick = () => {
        const newFlagValue = !cellFlagged;
        saveData(cellnumber, cellComment, cellnumberlastsaved, newFlagValue);
    };

    const flyoutDismissed = (ev: any): void => {
        setShowdetailsflyout(false);
    };

    const focusCellIfExists = (cellKey: string) => {
        // If cellKey is defined, it was found in the Map containing all cells
        // and it can be focused
        if (cellKey) {
            const destinationCell = cellCtx.inputNodes.get(cellKey);
            destinationCell.setFocus();
        }
    };

    const getKeyForTargetDay = (currentCellKey: string, daysFromCurrent: number) => {
        const keys = [...cellCtx.inputNodes.keys()];
        const indexOfCurrent = keys.indexOf(currentCellKey);

        return keys[indexOfCurrent + daysFromCurrent];
    };

    const focusBelowCell = (currentRowId: any, currentDay: any) => {
        const keys = [...cellCtx.inputNodes.keys()];
        let highest = 0;

        keys.some(function (key: string) {
            // get day index of the current key - what is after the last underscore
            const dayIndex: number = +key.substring(key.lastIndexOf('_') + 1);

            if (dayIndex >= highest) {
                highest = dayIndex;
            } else {
                // we found the highest dayIndex - return true to break .some() loop
                return true;
            }
        });

        const oneWeekAheadKey = getKeyForTargetDay(`${currentRowId}_${currentDay}`, highest + 1);
        focusCellIfExists(oneWeekAheadKey);
    };

    const focusAboveCell = (currentRowId: any, currentDay: any) => {
        const keys = [...cellCtx.inputNodes.keys()];
        const daysInPeriod = getDaysInWeek(keys);
        const oneWeekBeforeKey = getKeyForTargetDay(`${currentRowId}_${currentDay}`, -(daysInPeriod + 1));
        focusCellIfExists(oneWeekBeforeKey);
    };

    const onKeyDown = (event: any): void => {
        const workTypeId = props.time.workType == null || props.time.workType == undefined ? getEmptyGuid() : props.time.workType.id;
        const rowId = `${props.time.taskId}_${workTypeId}`;

        switch (event.keyCode) {
            case 13:
                // Enter key
                focusBelowCell(rowId, 0);
                break;

            case 37:
                // Left arrow
                // focusPreviousCell(rowId, props.day.id);
                focusAboveCell(rowId, 0);
                break;

            case 38:
                // Up arrow
                focusAboveCell(rowId, 0);
                break;

            case 39:
                // Right arrow
                // focusNextCell(rowId, props.day.id);
                focusBelowCell(rowId, 0);
                break;

            case 40:
                // Down arrow
                focusBelowCell(rowId, 0);
                break;

            default:
                return;
        }

        if (cellnumber !== cellnumberlastsaved || cellComment !== commentlastsaved) {
            if (cellIsError == '') {
                setDataSaved(true);
                saveData(cellnumber, cellComment, cellnumberlastsaved, cellFlagged);
                setAnyChanges(false);
            }
        }
    };

    const { mutate: save, isLoading } = useMutation({
        mutationFn: async ({
            time,
            periodDays,
            te,
            cellValue,
            allCellsValue,
            comment,
            flagged,
            prevVal,
        }: {
            time: UserRegistrationPeriod;
            periodDays: PeriodDay[];
            te: any;
            cellValue: number;
            allCellsValue: number;
            comment: string;
            flagged: boolean;
            prevVal: number;
        }) => {
            return props.updated(time, periodDays, te, cellValue, allCellsValue, comment, flagged, prevVal);
        },
        onSuccess: (result, { flagged }) => {
            setTimeout(() => {
                setDataSaved(false);
                setShowFlagIcon(flagged);
                setCellFlagged(flagged);
            }, 200);
        },
        onError: error => {
            console.error('Error saving data:', error);
            setCellIsError('There was an error saving the data.');
            setDataSaved(false);
        },
    });

    useEffect(() => {
        const onUnload = function (e) {
            e.preventDefault();
            e.returnValue = 'Unsaved changes will be lost!';
        };

        if (isLoading) {
            window.addEventListener('beforeunload', onUnload);
            return () => {
                window.removeEventListener('beforeunload', onUnload);
            };
        }
    }, [isLoading]);

    const saveData = (hours: number, comment: string, previousHours: number, isFlagged: boolean): void => {
        const hoursToBeSaved = hours;
        const commentToBeSaved = comment;
        const flagged = previousHours >= 0 && hoursToBeSaved === 0 ? false : isFlagged;

        if (!showdetailsflyout) {
            save({
                time: props.time,
                periodDays: workingDays,
                te: props.timeentries?.[0],
                cellValue: MathToFixed(hoursToBeSaved / workingDays.length, 2),
                allCellsValue: hoursToBeSaved,
                comment: commentToBeSaved,
                flagged,
                prevVal: previousHours,
            });

            setCellnumberlastsaved(hoursToBeSaved);
            setCommentlastsaved(commentToBeSaved);
        }
    };

    useImperativeHandle(ref, () => ({
        setFocus: () => textFieldRef.current && textFieldRef.current.focus(),
    }));

    return (
        <div className={styles.root}>
            {allowCellComment && (showdetailsicon || showdetailsflyout || cellComment !== '') ? (
                <FontIcon
                    id={targetId}
                    iconName="Comment"
                    className={'dayDetailsIcon' + (props.readonly ? ' dayDetailsIconDisabled' : '')}
                    onClick={onIconClick}
                />
            ) : null}
            {isLoading ? (
                <Stack horizontalAlign="center" verticalAlign="center" style={{ width: '100%', height: '100%' }}>
                    <Spinner size={SpinnerSize.small} />
                </Stack>
            ) : (
                <TooltipHost content={cellIsError !== '' ? cellIsError : props.readonly ? props.readonlyreason : ''} closeDelay={250}>
                    <TextField
                        componentRef={textFieldRef}
                        className="cellInput"
                        value={`${celltext !== '0' && celltext !== '' ? celltext : ''}${
                            gridInputType === 'percent' ? (!celltext || celltext === '0' || celltext === '' || textinfocus ? '' : ' %') : ''
                        }`}
                        onBlur={onBlur}
                        onFocus={onFocus}
                        onChange={onCellUpdated}
                        onKeyDown={onKeyDown}
                        disabled={props.readonly}
                        autoComplete="off"
                        style={{ textAlign: 'center' }}
                        styles={{
                            root: {
                                // margin: "0 auto",
                                // maxWidth: constants.cellInputMaxWidth,
                            },
                            fieldGroup: {
                                borderColor: `${cellIsError != '' ? 'rgba(155,42,49,0.75)' : 'rgba(0,0,0,0)'}`,
                                backgroundColor: `${dataSaved ? 'rgba(16, 124, 16, 0.6)!important' : 'rgba(0,0,0,0)'}`,
                                borderWidth: '2px',
                                '&:hover': {
                                    borderColor: `${cellIsError != '' ? 'rgba(155,42,49,1)' : 'rgba(0,0,0,0)'}`,
                                    backgroundColor: `${dataSaved ? 'rgba(16, 124, 16, 0.6)!important' : 'rgba(0,0,0,0)'}`,
                                    // backgroundColor: theme.semanticColors.inputBackground,
                                    // backgroundColor: workday ? theme.semanticColors.inputBackground : theme.palette.neutralLighterAlt,
                                },
                                '&::after': {
                                    borderColor: `${cellIsError != '' ? 'rgba(155,42,49,1)' : 'rgba(96,97,170,1)'}`,
                                },
                            },
                            field: {
                                textAlign: 'right',
                                paddingRight: theme.spacing.s1,
                                paddingLeft: 7,
                            },
                        }}
                    />

                    {allowCellFlag && showFlagIcon ? (
                        <EndPointSolidIcon
                            id={targetId}
                            style={{ color: cellFlagged ? theme.palette.red : theme.palette.themeLight, cursor: props.readonly ? 'no-drop' : 'pointer' }}
                            onClick={() => {
                                if (!props.readonly) {
                                    onFlagIconClick();
                                }
                            }}
                            className={'dayflagsIcon' + (props.readonly ? ' dayflagsIconDisabled' : '')}
                        />
                    ) : null}
                </TooltipHost>
            )}

            {showdetailsflyout && (
                <Callout
                    className={styles.callout}
                    gapSpace={0}
                    target={`#${targetId}`}
                    directionalHint={DirectionalHint.bottomCenter}
                    onDismiss={flyoutDismissed}
                    setInitialFocus
                    dismissOnTargetClick={true}
                >
                    <div className={styles.header}>
                        <Text className={styles.title}>{t('comment.title')}</Text>
                    </div>
                    <div className={styles.inner}>
                        <TextField value={cellComment} onChange={onCommentUpdated} multiline autoAdjustHeight disabled={props.readonly} />
                    </div>
                </Callout>
            )}
        </div>
    );
});

export default CellWeek;

const getStyles = makeStyles(theme => ({
    root: [
        'cellInner',
        {
            position: 'relative',
            height: '100%',
            width: '100%',
            maxWidth: constants.cellInputMaxWidthWeekOverview,
            margin: '0 auto',
            display: 'flex',
        },
    ],
    buttonArea: {
        verticalAlign: 'top',
        display: 'inline-block',
        textAlign: 'center',
        margin: '0 100px',
        minWidth: 130,
        height: 32,
    },
    callout: {
        maxWidth: 800,
        minWidth: 300,
    },
    header: {
        padding: '18px 24px 12px',
        color: theme.semanticColors.bodyText,
    },
    title: [
        theme.fonts.mediumPlus,
        {
            margin: 0,
            fontWeight: FontWeights.semilight,
        },
    ],
    inner: {
        height: '100%',
        padding: '0 24px 20px',
    },
    actions: {
        position: 'relative',
        marginTop: 20,
        width: '100%',
        whiteSpace: 'nowrap',
    },
    subtext: [
        theme.fonts.small,
        {
            margin: 0,
            fontWeight: FontWeights.semilight,
        },
    ],
    link: [
        theme.fonts.medium,
        {
            color: theme.palette.neutralPrimary,
        },
    ],
}));

function getDaysInWeek(keys: any[]) {
    const indexes: number[] = [];

    keys.forEach((key: string) => {
        // convert dayIndex to number
        const dayIndex: number = +key.substring(key.lastIndexOf('_') + 1);
        indexes.push(dayIndex);
    });

    // this indicates how many days we have in the period
    const daysInWeek = Math.max(...indexes);
    return daysInWeek;
}

export const isAllowedKey = (e: any) => {
    const allowed = '1234567890,.';

    if (allowed.includes(e.nativeEvent.data) || !e.nativeEvent.data) {
        return true;
    } else {
        return false;
    }
};
